import { GetStaticPropsContext } from 'next';

import { CareersSection } from '~/features/careers/components/careers-section';
import { getCareers } from '~/features/careers/controller/get-careers';
import { Careers } from '~/features/careers/models/careers';
import { DEFAULT_LANGUAGE } from '~/features/internationalization/languages';
import { PageHead } from '~/features/page/components/page-head';
import { PageLayout } from '~/features/page/components/page-layout';
import { getPage } from '~/features/page/controllers/get-page';
import { Page } from '~/features/page/models/page';
import { getPageTemplate } from '~/features/page-template/controllers/get-page-template';
import { AwardsSection } from '~/features/page-template/templates/about-us/components/awards-section';
import { MeetMentorsSection } from '~/features/page-template/templates/become-a-mentor/components/meet-mentors-section';
import { MentorBenefitsSection } from '~/features/page-template/templates/become-a-mentor/components/mentor-benefits-section';
import { MentorHeroSection } from '~/features/page-template/templates/become-a-mentor/components/mentor-hero-section';
import { MentorQualitiesSection } from '~/features/page-template/templates/become-a-mentor/components/mentor-qualities-section';
import { MentorRolesSection } from '~/features/page-template/templates/become-a-mentor/components/mentor-roles-section';
import { MentorStepsSection } from '~/features/page-template/templates/become-a-mentor/components/mentor-steps-section';
import {
  becomeAMentorQuery,
  BecomeAMentorTemplate,
  becomeAMentorTemplateSchema,
} from '~/features/page-template/templates/become-a-mentor/model';
import { useI18n } from '~/hooks/use-i18n';

type BecomeAMentorPageProps = {
  page: Page;
  becomeAMentorTemplate: BecomeAMentorTemplate;
  careers: Careers;
};

export default function BecomeAMentorPage({ page, becomeAMentorTemplate, careers }: BecomeAMentorPageProps) {
  const {
    pageTitle,
    heroImage,
    heroDescription,
    meetOurMentorsSectionTitle,
    meetOurMentorsSectionDescription,
    meetOurMentorsSectionImage,
    meetOurMentorsSectionCTA,
    mentorBenefitsTitle,
    mentorBenefits,
    mentorPartners,
    mentorRolesTitle,
    mentorRolesDescription,
    mentorRolesTabs,
    mentorRolesBenefits,
    mentorQualitiesTitle,
    mentorQualities,
    mentorQualitiesStats,
    mentorStepsTitle,
    mentorSteps,
    mentorStepsCTA,
    mentorAwardsTitle,
    mentorAwards,
  } = becomeAMentorTemplate;

  const { t } = useI18n();

  const translatedMentorBenefits = [
    {
      title: t('becomeAMentor.mentorBenefits.0.title'),
      glyphName: 'person-laptop-one',
    },
    {
      title: t('becomeAMentor.mentorBenefits.1.title'),
      glyphName: 'medal-star-one',
    },
    {
      title: t('becomeAMentor.mentorBenefits.2.title'),
      glyphName: 'graduation-cap-two',
    },
    {
      title: t('becomeAMentor.mentorBenefits.3.title'),
      glyphName: 'network-seven-one',
    },
  ];

  const translatedMentorRolesBenefits = [
    {
      title: t('becomeAMentor.mentorRolesBenefits.0.title'),
      description: t('becomeAMentor.mentorRolesBenefits.0.description'),
    },
    {
      title: t('becomeAMentor.mentorRolesBenefits.1.title'),
      description: t('becomeAMentor.mentorRolesBenefits.1.description'),
    },
    {
      title: t('becomeAMentor.mentorRolesBenefits.2.title'),
      description: t('becomeAMentor.mentorRolesBenefits.2.description'),
    },
    {
      title: t('becomeAMentor.mentorRolesBenefits.3.title'),
      description: t('becomeAMentor.mentorRolesBenefits.3.description'),
    },
  ];

  const translatedMentorRolesTabs = [
    {
      title: t('becomeAMentor.mentorRolesTabs.0.title'),
      description: t('becomeAMentor.mentorRolesTabs.0.description'),
      cta: {
        buttonVariant: 'outline',
        buttonColorScheme: 'blue',
        buttonLabel: t('becomeAMentor.mentorRolesTabs.0.cta.buttonLabel'),
        buttonAction: 'Custom URL',
        customUrl: '#open-positions',
      },
      glyphName: 'person-podium-three',
    },
    {
      title: t('becomeAMentor.mentorRolesTabs.1.title'),
      description: t('becomeAMentor.mentorRolesTabs.1.description'),
      cta: {
        buttonVariant: 'outline',
        buttonColorScheme: 'blue',
        buttonLabel: t('becomeAMentor.mentorRolesTabs.1.cta.buttonLabel'),
        buttonAction: 'Custom URL',
        customUrl: '#open-positions',
      },
      glyphName: 'mountain-one',
    },
    {
      title: t('becomeAMentor.mentorRolesTabs.2.title'),
      description: t('becomeAMentor.mentorRolesTabs.2.description'),
      cta: {
        buttonVariant: 'outline',
        buttonColorScheme: 'blue',
        buttonLabel: t('becomeAMentor.mentorRolesTabs.2.cta.buttonLabel'),
        buttonAction: 'Custom URL',
        customUrl: '#open-positions',
      },
      glyphName: 'hand-plant-one',
    },
    {
      title: t('becomeAMentor.mentorRolesTabs.3.title'),
      description: t('becomeAMentor.mentorRolesTabs.3.description'),
      cta: {
        buttonVariant: 'outline',
        buttonColorScheme: 'blue',
        buttonLabel: t('becomeAMentor.mentorRolesTabs.3.cta.buttonLabel'),
        buttonAction: 'Custom URL',
        customUrl: '#open-positions',
      },
      glyphName: 'lightbulb-head-one',
    },
    {
      title: t('becomeAMentor.mentorRolesTabs.4.title'),
      description: t('becomeAMentor.mentorRolesTabs.4.description'),
      cta: {
        buttonVariant: 'outline',
        buttonColorScheme: 'blue',
        buttonLabel: t('becomeAMentor.mentorRolesTabs.4.cta.buttonLabel'),
        buttonAction: 'Custom URL',
        customUrl: '#open-positions',
      },
      glyphName: 'magnifying-glass-check-mark-two',
    },
  ];

  const translatedMentorQualities = [
    {
      title: t('becomeAMentor.mentorQualities.0.title'),
      description: t('becomeAMentor.mentorQualities.0.description'),
      glyphName: 'person-shield-stars-two',
    },
    {
      title: t('becomeAMentor.mentorQualities.1.title'),
      description: t('becomeAMentor.mentorQualities.1.description'),
      glyphName: 'hands-shaking-one',
    },
    {
      title: t('becomeAMentor.mentorQualities.2.title'),
      description: t('becomeAMentor.mentorQualities.2.description'),
      glyphName: 'heart-path-three',
    },
  ];

  const translatedMentorQualitiesStats = [
    {
      title: t('becomeAMentor.mentorQualitiesStats.0.title'),
      value: t('becomeAMentor.mentorQualitiesStats.0.value'),
    },
    {
      title: t('becomeAMentor.mentorQualitiesStats.1.title'),
      value: t('becomeAMentor.mentorQualitiesStats.1.value'),
    },
    {
      title: t('becomeAMentor.mentorQualitiesStats.2.title'),
      value: t('becomeAMentor.mentorQualitiesStats.2.value'),
    },
    {
      title: t('becomeAMentor.mentorQualitiesStats.3.title'),
      value: t('becomeAMentor.mentorQualitiesStats.3.value'),
    },
  ];

  const translatedMentorSteps = [
    {
      title: t('becomeAMentor.mentorSteps.0.title'),
      text: t('becomeAMentor.mentorSteps.0.text'),
    },
    {
      title: t('becomeAMentor.mentorSteps.1.title'),
      text: t('becomeAMentor.mentorSteps.1.text'),
    },
    {
      title: t('becomeAMentor.mentorSteps.2.title'),
      text: t('becomeAMentor.mentorSteps.2.text'),
    },
    {
      title: t('becomeAMentor.mentorSteps.3.title'),
      text: t('becomeAMentor.mentorSteps.3.text'),
    },
  ];

  return (
    <>
      <PageHead page={page} />
      <PageLayout withoutVerticalPadding>
        <MentorHeroSection
          ctaText={t('becomeAMentor.ctaText')}
          heroDescription={t('becomeAMentor.heroDescription')}
          heroImage={heroImage}
          pageTitle={t('becomeAMentor.pageTitle')}
        />
        <MeetMentorsSection
          cta={{ ...meetOurMentorsSectionCTA, buttonLabel: t('becomeAMentor.meetOurMentorsSectionCTA.buttonLabel') }}
          description={t('becomeAMentor.meetOurMentorsSectionDescription')}
          image={meetOurMentorsSectionImage}
          title={t('becomeAMentor.meetOurMentorsSectionTitle')}
        />
        <MentorBenefitsSection
          benefits={translatedMentorBenefits}
          partners={mentorPartners}
          partnersDescription={t('becomeAMentor.mentorBenefitsPartnersDescription')}
          title={t('becomeAMentor.mentorBenefitsTitle')}
        />
        <MentorRolesSection
          benefits={translatedMentorRolesBenefits}
          benefitsTitle={t('becomeAMentor.mentorRolesBenefitsTitle')}
          description={t('becomeAMentor.mentorRolesDescription')}
          roles={translatedMentorRolesTabs}
          title={t('becomeAMentor.mentorRolesTitle')}
        />
        <MentorQualitiesSection
          qualities={translatedMentorQualities}
          stats={translatedMentorQualitiesStats}
          title={t('becomeAMentor.mentorQualitiesTitle')}
        />
        <MentorStepsSection
          cta={{ ...mentorStepsCTA, buttonLabel: t('becomeAMentor.mentorStepsCTA.buttonLabel') }}
          steps={translatedMentorSteps}
          title={t('becomeAMentor.mentorStepsTitle')}
        />
        <CareersSection careers={careers} title={t('becomeAMentor.careersTitle')} />
        <AwardsSection awards={mentorAwards} title={t('becomeAMentor.awardsTitle')!} />
      </PageLayout>
    </>
  );
}

export async function getStaticProps({ locale }: GetStaticPropsContext) {
  // Stop additional locale pages except the default locale from being built
  if (locale !== DEFAULT_LANGUAGE) {
    return {
      notFound: true,
    };
  }

  const [page, becomeAMentorTemplate, careers] = await Promise.all([
    getPage('/become-a-mentor'),
    getPageTemplate<BecomeAMentorTemplate>({
      templateName: 'Become a mentor',
      templateQuery: becomeAMentorQuery,
      templateZodSchema: becomeAMentorTemplateSchema,
    }),
    getCareers(),
  ]);

  return {
    props: {
      page,
      becomeAMentorTemplate,
      careers,
    },
    revalidate: 60 * 60 * 12, // Note: revalidate every 12 hours
  };
}
